import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  startOfYear, endOfYear, format, differenceInCalendarDays,
} from 'date-fns'
import {
  BarChart3, Download, Users as UsersIcon, ChevronDown, Search, Check, ArrowRight, MoreHorizontal,
} from 'lucide-react'
import { exportAttendanceSheet } from '@/api/hrAnalytics'
import {
  getAttendanceSummary, getAttendanceTimeseries,
  type EmployeeAttendanceSummary, type AttendanceTimeseriesPoint,
} from '@/api/analytics'
import { listUsers } from '@/api/users'
import { listDepartments, type Department } from '@/api/orgstructure'
import type { User } from '@/types'
import { cn } from '@/lib/utils'
import SectionCard from '@/components/dashboard/SectionCard'
import StatTile from '@/components/dashboard/StatTile'
import BarListWidget from '@/components/dashboard/BarListWidget'
import TrendChart from '@/components/dashboard/TrendChart'
import DetailModal from '@/components/dashboard/DetailModal'
import type { ListWidgetRow } from '@/components/dashboard/ListWidget'

const LEAVE_TYPE_LABELS: Record<string, string> = {
  casual: 'Casual', sick: 'Sick', urgent: 'Urgent', annual: 'Annual', wfh: 'WFH',
}

type Preset = 'today' | 'week' | 'month' | 'year' | 'custom'
const iso = (d: Date) => format(d, 'yyyy-MM-dd')

function presetRange(p: Preset): { start: string; end: string } {
  const now = new Date()
  switch (p) {
    case 'today': return { start: iso(now), end: iso(now) }
    case 'week': return { start: iso(startOfWeek(now, { weekStartsOn: 1 })), end: iso(endOfWeek(now, { weekStartsOn: 1 })) }
    case 'month': return { start: iso(startOfMonth(now)), end: iso(endOfMonth(now)) }
    case 'year': return { start: iso(startOfYear(now)), end: iso(endOfYear(now)) }
    default: return { start: iso(startOfMonth(now)), end: iso(endOfMonth(now)) }
  }
}

function leaveBreakdownText(types: Record<string, number>): string {
  const parts = Object.entries(types)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${LEAVE_TYPE_LABELS[k] || k} ${n}`)
  return parts.join(' · ') || 'No leave taken'
}

function fmtMinutes(mins: number): string {
  const h = Math.floor(Math.abs(mins) / 60)
  const m = Math.round(Math.abs(mins) % 60)
  return `${h}h${m ? ` ${m}m` : ''}`
}

/* ─────────────────────────────── main page ─────────────────────────────── */

export default function HRAnalytics() {
  const { orgSlug } = useParams<{ orgSlug: string }>()
  const base = `/${orgSlug}`
  const [preset, setPreset] = useState<Preset>('week')
  const [range, setRange] = useState(() => presetRange('week'))
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [departmentId, setDepartmentId] = useState('')
  const [empOpen, setEmpOpen] = useState(false)
  const [empSearch, setEmpSearch] = useState('')
  const [exporting, setExporting] = useState(false)
  const [detail, setDetail] = useState<{ title: string; rows: ListWidgetRow[] } | null>(null)

  const pickPreset = (p: Preset) => {
    setPreset(p)
    if (p !== 'custom') setRange(presetRange(p))
  }

  const userIds = selected.size ? [...selected] : undefined

  const roster = useQuery({
    queryKey: ['hr-roster'],
    queryFn: () => listUsers({ per_page: 500, is_active: true }), // backend caps per_page at 500
    staleTime: 60_000,
  })
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })

  const summary = useQuery({
    queryKey: ['hr-summary', range.start, range.end, userIds, departmentId],
    queryFn: () => getAttendanceSummary(range.start, range.end, userIds, departmentId || undefined),
  })

  const spanDays = differenceInCalendarDays(new Date(range.end), new Date(range.start))
  const granularity: 'day' | 'week' | 'month' = spanDays > 120 ? 'month' : spanDays > 30 ? 'week' : 'day'
  const trend = useQuery({
    queryKey: ['hr-trend', range.start, range.end, granularity],
    queryFn: () => getAttendanceTimeseries(range.start, range.end, granularity),
  })

  const doExport = async () => {
    setExporting(true)
    try {
      await exportAttendanceSheet({ start: range.start, end: range.end, user_ids: userIds, department_id: departmentId || undefined })
    } finally { setExporting(false) }
  }

  const rosterUsers = (roster.data?.items ?? []).filter((u: User) =>
    !empSearch.trim() || u.name.toLowerCase().includes(empSearch.toLowerCase()))
  const toggleEmp = (id: string) => setSelected((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id); else n.add(id)
    return n
  })

  const totals = summary.data?.totals
  const employees = summary.data?.employees ?? []

  const openCategory = (
    title: string,
    filterFn: (e: EmployeeAttendanceSummary) => boolean,
    subtitleFn: (e: EmployeeAttendanceSummary) => string,
  ) => {
    setDetail({
      title,
      rows: employees.filter(filterFn).map((e: EmployeeAttendanceSummary) => ({
        id: e.user_id, name: e.name, subtitle: subtitleFn(e),
      })),
    })
  }

  const leaveTypeCounts: Record<string, number> = totals?.leave_type_days || {}
  const leaveTypeBars = Object.entries(leaveTypeCounts)
    .map(([label, count]: [string, number]) => ({ label: LEAVE_TYPE_LABELS[label] || label, count }))

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-brand" />
          <h1 className="text-xl font-bold text-gray-900">HR Analytics</h1>
        </div>
        <button onClick={doExport} disabled={exporting || summary.isLoading}
          title="Download the weekly attendance sheet (Excel)"
          className="flex items-center gap-1.5 rounded-lg bg-[#1E2A3A] px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
          <Download className="h-4 w-4" />{exporting ? 'Exporting…' : 'Export XLSX'}
        </button>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        {(['today', 'week', 'month', 'year', 'custom'] as Preset[]).map((p) => (
          <button key={p} onClick={() => pickPreset(p)}
            className={cn('rounded-lg px-3 py-1.5 text-sm font-medium capitalize transition',
              preset === p ? 'bg-brand text-white' : 'bg-white text-gray-600 ring-1 ring-black/5 hover:bg-gray-50')}>
            {p === 'today' ? 'Today' : p === 'week' ? 'This Week' : p === 'month' ? 'This Month' : p === 'year' ? 'This Year' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-1.5">
            <input type="date" value={range.start} max={range.end}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
            <span className="text-gray-400">→</span>
            <input type="date" value={range.end} min={range.start}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
          </div>
        )}

        <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}
          className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm text-gray-600">
          <option value="">All departments</option>
          {(departments.data ?? []).map((d: Department) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>

        {/* Employee filter */}
        <div className="relative ml-auto">
          <button onClick={() => setEmpOpen((o) => !o)}
            className="flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-gray-600 ring-1 ring-black/5 hover:bg-gray-50">
            <UsersIcon className="h-4 w-4" />
            {selected.size ? `${selected.size} selected` : 'All employees'}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          {empOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setEmpOpen(false)} />
              <div className="absolute right-0 z-50 mt-1 w-64 rounded-lg bg-white p-2 shadow-lg ring-1 ring-black/5">
                <div className="mb-2 flex items-center gap-2 rounded-md bg-gray-50 px-2">
                  <Search className="h-3.5 w-3.5 text-gray-400" />
                  <input value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} placeholder="Search…"
                    className="w-full bg-transparent py-1.5 text-sm outline-none" />
                </div>
                <div className="flex items-center justify-between px-1 pb-1">
                  <button onClick={() => setSelected(new Set(rosterUsers.map((u: User) => u.id)))} className="text-xs font-medium text-brand">Select all</button>
                  {selected.size > 0 && <button onClick={() => setSelected(new Set())} className="text-xs text-gray-400">Clear</button>}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {rosterUsers.map((u: User) => (
                    <button key={u.id} onClick={() => toggleEmp(u.id)}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-50">
                      <span className={cn('flex h-4 w-4 items-center justify-center rounded border',
                        selected.has(u.id) ? 'border-brand bg-brand text-white' : 'border-gray-300')}>
                        {selected.has(u.id) && <Check className="h-3 w-3" />}
                      </span>
                      <span className="truncate text-gray-700">{u.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Org-wide stat tiles */}
      <SectionCard title="Attendance Overview" action={<span className="text-xs text-gray-400">{range.start} → {range.end}</span>}>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <StatTile label="Present" value={totals?.present_days ?? '—'} tone="green"
            onClick={() => openCategory('Present', (e) => e.present_days > 0, (e) => `${e.present_days} day(s) present`)} />
          <StatTile label="Absent" value={totals?.absent_days ?? '—'} tone="red"
            onClick={() => openCategory('Absent', (e) => e.absent_days > 0, (e) => `${e.absent_days} day(s) absent`)} />
          <StatTile label="On Leave" value={totals?.leave_days ?? '—'} tone="amber"
            onClick={() => openCategory('On Leave', (e) => e.leave_days > 0, (e) => leaveBreakdownText(e.leave_type_days))} />
          <StatTile label="WFH" value={totals?.wfh_days ?? '—'} tone="purple"
            onClick={() => openCategory('Work From Home', (e) => e.wfh_days > 0, (e) => `${e.wfh_days} day(s) WFH`)} />
          <StatTile label="Late Arrivals" value={totals?.late_days ?? '—'} tone="orange"
            onClick={() => openCategory('Late Arrivals', (e) => e.late_days > 0, (e) => `${e.late_days} day(s) late`)} />
          <StatTile label="Overtime" value={totals ? `${totals.overtime_hours}h` : '—'} tone="indigo"
            onClick={() => openCategory('Overtime', (e) => e.overtime_days > 0, (e) => `${e.overtime_hours}h overtime`)} />
          <StatTile label="Avg Hrs / Day" value={totals ? `${totals.avg_hours_per_present_day}h` : '—'} tone="blue" />
        </div>
      </SectionCard>

      {/* Trend + leave-by-type */}
      <div className="grid lg:grid-cols-2 gap-5">
        <SectionCard title="Attendance Trend" action={<span className="text-xs text-gray-400 capitalize">{granularity}ly · org-wide</span>}>
          <TrendChart
            type="area"
            data={(trend.data || []).map((p: AttendanceTimeseriesPoint) => ({ label: p.label, Present: p.present, Absent: p.absent, Leave: p.leave }))}
            series={[
              { key: 'Present', label: 'Present', color: '#22c55e' },
              { key: 'Absent', label: 'Absent', color: '#ef4444' },
              { key: 'Leave', label: 'Leave', color: '#f59e0b' },
            ]}
          />
        </SectionCard>
        <SectionCard title="Leave By Type">
          <BarListWidget data={leaveTypeBars} />
        </SectionCard>
      </div>

      {/* Per-employee list */}
      <SectionCard title="Employees" action={<span className="text-xs text-gray-400">{employees.length} shown · click the arrow for full details</span>} bodyClassName="p-0">
        {summary.isLoading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : summary.isError ? (
          <div className="px-4 py-10 text-center text-sm text-red-500">Failed to load attendance data.</div>
        ) : employees.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">No employees match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-4 px-4 min-w-[900px]">
              {employees.map((e: EmployeeAttendanceSummary, i: number) => (
                <EmployeeRow key={e.user_id} e={e} base={base} range={range} isLast={i === employees.length - 1} />
              ))}
            </div>
          </div>
        )}
      </SectionCard>

      {detail && <DetailModal title={detail.title} rows={detail.rows} onClose={() => setDetail(null)} />}
    </div>
  )
}

/* ─────────────────────────────── employee row ─────────────────────────────── */

const QUICK_PILLS: { key: keyof EmployeeAttendanceSummary; label: string; tone: string }[] = [
  { key: 'present_days', label: 'Present', tone: 'bg-green-100 text-green-700' },
  { key: 'absent_days', label: 'Absent', tone: 'bg-red-100 text-red-700' },
  { key: 'leave_days', label: 'Leave', tone: 'bg-amber-100 text-amber-700' },
  { key: 'early_left_days', label: 'Early Left', tone: 'bg-pink-100 text-pink-700' },
  { key: 'late_days', label: 'Late Arrival', tone: 'bg-orange-100 text-orange-700' },
]
const MORE_PILLS: { key: keyof EmployeeAttendanceSummary; label: string }[] = [
  { key: 'wfh_days', label: 'WFH' },
  { key: 'overtime_days', label: 'Overtime Days' },
  { key: 'less_hours_days', label: 'Short Hours' },
  { key: 'holiday_days', label: 'Holiday' },
  { key: 'off_days', label: 'Days Off' },
  { key: 'scheduled_working_days', label: 'Scheduled Days' },
]

function EmployeeRow({ e, base, range, isLast }: { e: EmployeeAttendanceSummary; base: string; range: { start: string; end: string }; isLast: boolean }) {
  // Per-day shortfall from the server (leave/holidays excluded; overtime doesn't offset missed days).
  const shortMinutes = e.short_minutes ?? Math.max(0, e.expected_minutes - e.worked_minutes)
  const progressPct = e.expected_minutes > 0 ? Math.min(100, Math.round((e.worked_minutes / e.expected_minutes) * 100)) : 0
  const initials = e.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()

  return (
    <>
      <div className="py-3.5 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 shrink-0 rounded-full bg-brand/15 text-brand text-sm font-semibold flex items-center justify-center">
            {initials}
          </span>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-gray-800">{e.name}</span>
              {e.employee_id && <span className="shrink-0 rounded-full bg-gray-100 text-gray-500 text-[10px] font-semibold px-1.5 py-0.5">{e.employee_id}</span>}
            </div>
            <p className="text-xs text-gray-400">{[e.department, e.designation].filter(Boolean).join(' · ') || e.role.replace('_', ' ')}</p>
          </div>
        </div>
      </div>

      <div className="py-3.5 min-w-[220px]">
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${progressPct}%` }} />
        </div>
        <div className="grid grid-cols-4 gap-x-2 text-[11px] text-gray-400 mt-1 whitespace-nowrap">
          <span>Expected: <span className="text-gray-600 font-medium">{fmtMinutes(e.expected_minutes)}</span></span>
          <span>Worked: <span className="text-gray-600 font-medium">{fmtMinutes(e.worked_minutes)}</span></span>
          <span>Short: <span className="text-gray-600 font-medium">{fmtMinutes(shortMinutes)}</span></span>
          <span>Overtime: <span className="text-gray-600 font-medium">{fmtMinutes(e.overtime_minutes)}</span></span>
        </div>
      </div>

      <div className="py-3.5 whitespace-nowrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {QUICK_PILLS.map((p) => (
            <span key={p.key} className={cn('rounded-full px-2.5 py-1 text-xs font-medium', p.tone)}>
              {e[p.key] as number} <span className="opacity-70">{p.label}</span>
            </span>
          ))}
          <MoreStatsPopover e={e} />
        </div>
      </div>

      <div className="py-3.5 whitespace-nowrap text-right">
        <Link
          to={`${base}/admin/analytics/${e.user_id}?start=${range.start}&end=${range.end}`}
          title="View full attendance details"
          className="inline-flex shrink-0 rounded-full p-2 text-gray-400 hover:text-brand hover:bg-brand/10 transition-colors"
        >
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {!isLast && <div className="col-span-4 border-b border-gray-100" />}
    </>
  )
}

function MoreStatsPopover({ e }: { e: EmployeeAttendanceSummary }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-200">
        <MoreHorizontal className="h-3.5 w-3.5" /> More Stats
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-56 rounded-lg bg-white p-3 shadow-lg ring-1 ring-black/5">
            <p className="text-xs font-semibold text-gray-500 mb-2">All Attendance Stats</p>
            <div className="grid grid-cols-2 gap-2">
              {[...QUICK_PILLS.map((p) => ({ key: p.key, label: p.label })), ...MORE_PILLS].map((p) => (
                <div key={p.key} className="rounded-md bg-gray-50 px-2 py-1.5 text-center">
                  <div className="text-sm font-bold text-gray-800">{e[p.key] as number}</div>
                  <div className="text-[10px] text-gray-400">{p.label}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
